import { useEffect, useState } from "react";
import { Gift, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { fmtCredits } from "../lib/api";

/**
 * Aviso de "invita y gana", bajo la cabecera de Mis Canales.
 *
 * Ocupa el MISMO sitio y la misma forma que el aviso de premios (LogrosRail): franja a ancho
 * completo bajo la cabecera. Los dos nunca coinciden en pantalla —premios dentro de un canal,
 * invitación en Mis Canales— así que compartir aspecto no confunde: convierte esa franja en un
 * sitio fijo y reconocible donde el producto cuenta lo que se puede ganar.
 *
 * Va en Mis Canales y no dentro de un canal a propósito: ahí el usuario está a punto de hacer un
 * vídeo y manda el contador de premios, que sí está pegado a esa acción. Mis Canales es el
 * vestíbulo, sin tarea a medias, y es donde una invitación no compite con nada.
 *
 * Se calla en cuanto el usuario ya ha traído a alguien: quien ya usa el programa no necesita el
 * recordatorio en cada visita, y la franja pasa a valer más vacía.
 *
 * Los datos vienen de su propia llamada y no de `quota` a posta: `quota` se sondea cada pocos
 * segundos y no merece cargar con una consulta más; esta solo dispara al entrar en Mis Canales.
 */
export default function ReferidoBanner() {
  const [d, setD] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let vivo = true;
    api.get("/referidos").then((r) => { if (vivo) setD(r.data); }).catch(() => {});
    return () => { vivo = false; };
  }, []);

  if (!d || d.referidos?.length > 0) return null;

  const mejor = Math.max(0, ...Object.values(d.premios || {}));
  if (!mejor) return null;

  return (
    <div data-testid="referido-banner" className="bg-emerald-50 border-b border-emerald-200/80">
      {/* En móvil todo en UNA línea (flex-nowrap), igual que el aviso de premios: una franja de
          tres renglones sobre el contenido es demasiado para algo que solo informa. Desde sm
          vuelve el wrap normal, que ahí ya cabe la frase entera. */}
      <button onClick={() => navigate("/app/referidos")}
        className="w-full max-w-7xl mx-auto px-4 sm:px-8 py-2.5 flex flex-nowrap sm:flex-wrap
                   items-center gap-x-2 sm:gap-x-4 gap-y-2 text-left transition-colors
                   hover:bg-emerald-100/60 focus:outline-none focus-visible:ring-2
                   focus-visible:ring-inset focus-visible:ring-emerald-600">
        <span className="grid shrink-0 place-items-center h-8 w-8 rounded-xl bg-emerald-100 text-emerald-600"
          aria-hidden="true">
          <Gift size={17} strokeWidth={2.5} />
        </span>

        {/* Dos redacciones de lo MISMO, no dos mensajes: la corta conserva la cifra que importa
            —cuánto se puede ganar— y suelta el resto, que es lo único que no cabe en 320px. */}
        <span className="sm:hidden text-[clamp(11px,3.2vw,14px)] font-extrabold whitespace-nowrap text-emerald-950">
          Invita y gana hasta {fmtCredits(mejor)} créditos
        </span>

        <span className="hidden sm:block text-base text-emerald-900 min-w-0">
          <b className="font-extrabold">Invita y gana hasta {fmtCredits(mejor)} créditos.</b>{" "}
          <span className="text-emerald-800">
            Cuando alguien contrate un plan con tu enlace, él entra
            con {fmtCredits(d.bienvenida)} de regalo.
          </span>
        </span>

        <span className="ml-auto shrink-0 inline-flex items-center gap-1.5 text-sm sm:text-base
                         font-bold text-emerald-700">
          <span className="hidden sm:inline">Ver mi enlace</span>
          <ArrowRight size={17} />
        </span>
      </button>
    </div>
  );
}
